import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';
import RegionsPlugin from 'wavesurfer.js/dist/plugins/regions.esm.js';
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js';

import { BiZoomIn, BiZoomOut, BiPause } from 'react-icons/bi';
import { BsPlayFill } from 'react-icons/bs';
import AudioVolumeRange from '@app/components/audio-build/waveaudio/audio-volume-range';
import useTranslation from '@app/hooks/use-translation';
import connector from '@app/utils/store';
import { CircleButton, Toggle, TooltipWrapper } from '@ui';
import twTheme from '@app/themes';

import { emotionToColorConverter } from '@app/constants/emotions';
import { useAudioContext } from '@app/components/audio-build/record-provider';

import { useLazyDownloadRecordQuery } from '@app/store/api/records.api';

import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';

import { WAVE_COMPONENT_CONFIG } from '../data/audio-data';

import PlaybackSpeedSelect from './playback-speed-select';
import { circleBlueButtonClassName } from './wave-audio.styles';
import { WaveAudioProps } from './wave-audio.types';

const WaveAudio: FC<WaveAudioProps> = (props) => {
  const {
    activeMetricWords,
    onChangeActiveMessageId,
    activeMessageId,
    activeChartTiming,
    dialogs,
    audio,
    wordsTimeList,
    channelSettings,
    changeFullScreen,
    recordId,
    emotionTrack,
  } = props;

  const { data: userInfo } = useGetUserInfoQuery();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const timelineRef = useRef<HTMLDivElement | null>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const regionsPluginRef = useRef<any>(null);

  // keep direct references to region objects so we can remove them selectively
  const markerRegionsRef = useRef<any[]>([]);
  const emotionRegionsRef = useRef<any[]>([]);
  const timelinePluginRef = useRef<any>(null);
  const selectionRegionRef = useRef<any | null>(null);

  const [durationTime, setDurationTime] = useState<string>(WAVE_COMPONENT_CONFIG.initialTimeFormat);
  const [fullTime, setFullTime] = useState<string>(WAVE_COMPONENT_CONFIG.initialTimeFormat);
  const [isPause, setIsPause] = useState<boolean>(true);
  const [currentZoom, setCurrentZoom] = useState<number>(0);

  const { toggleEmotions, changeToggleEmotions, currentTemplate } = useAudioContext();
  const [downloadAudio] = useLazyDownloadRecordQuery();
  const { t } = useTranslation('components.audio');

  function getTimelineConfig(duration: number) {
    const targetTicks = 5;

    const rawStep = duration / targetTicks;

    const niceSteps = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

    const step = niceSteps.find((s) => s >= rawStep) || 3600;

    return {
      timeInterval: step,
      primaryLabelInterval: step,
      secondaryLabelInterval: 0,
    };
  }
  const formatTime = useCallback((timeSeconds: number) => {
    const hours = Math.floor(timeSeconds / 3600);
    const minutes = Math.floor((timeSeconds - hours * 3600) / 60);
    const seconds = Math.ceil(timeSeconds - hours * 3600 - minutes * 60);
    const parts = [hours, minutes, seconds];
    const out = (hours ? parts : parts.slice(1)).map((n) => ('00' + n).slice(-2)).join(':');
    return out;
  }, []);

  const createSelectionRegion = useCallback(
    (regionData: { id: string; start: number; end: number; color?: string }) => {
      const regionsPlugin = regionsPluginRef.current;
      if (!regionsPlugin) return;

      // remove previous selection
      if (selectionRegionRef.current) {
        try {
          selectionRegionRef.current.remove();
        } catch (e) {
          console.error(e);
        }
        selectionRegionRef.current = null;
      }

      try {
        const r = regionsPlugin.addRegion({
          id: regionData.id,
          start: regionData.start,
          end: regionData.end,
          color: regionData.color ?? WAVE_COMPONENT_CONFIG.defaultRegionColor,
          drag: false,
          resize: false,
          data: { systemRegionId: -1 },
        });
        selectionRegionRef.current = r;
      } catch (e) {
        // ignore
      }
    },
    [],
  );

  const handleFindChartRegionId = useCallback(() => {
    const ws = wavesurferRef.current;
    if (!ws || !dialogs?.length) return;

    const currentTimeSec = ws.getCurrentTime?.() ?? 0;
    const timeMarker = currentTimeSec * WAVE_COMPONENT_CONFIG.currentTimingMs; // in ms

    const foundedRegions = dialogs.filter((textItem) => {
      const from = Math.round(textItem.from);
      const to = Math.round(textItem.to || 0);
      return from <= timeMarker && to >= timeMarker;
    });

    if (foundedRegions.length) {
      const ids = foundedRegions.map((r) => r.id);

      const isDifferent =
        !activeMessageId ||
        ids.length !== activeMessageId.length ||
        ids.some((id) => !activeMessageId.includes(id));

      if (isDifferent) {
        onChangeActiveMessageId?.(ids);

        const first = foundedRegions[0];
        createSelectionRegion({
          id: `${WAVE_COMPONENT_CONFIG.regionPrefix}-${first.id}`,
          start: first.from / WAVE_COMPONENT_CONFIG.currentTimingMs,
          end: (first.to || first.from + 1) / WAVE_COMPONENT_CONFIG.currentTimingMs,
          color: WAVE_COMPONENT_CONFIG.defaultRegionColor,
        });
      }
    }
  }, [dialogs, onChangeActiveMessageId, activeMessageId, createSelectionRegion]);

  // const regionResetHandler = useCallback(() => {
  //   if (selectionRegionRef.current) {
  //     try {
  //       selectionRegionRef.current.remove();
  //     } catch (e) {
  //       console.error(e);
  //     }
  //     selectionRegionRef.current = null;
  //   }
  // }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const splitChannels = Array(WAVE_COMPONENT_CONFIG.channelsLimit)
      .fill(0)
      .map(() => ({
        waveColor: '#a15db1',
        progressColor: '#7A4487',
        height: 70,
      }));

    const ws = WaveSurfer.create({
      container: containerRef.current,
      waveColor: '#a15db1',
      progressColor: '#7A4487',
      cursorColor: twTheme.action,
      cursorWidth: 1.5,
      height: 70,
      barWidth: 2,
      barRadius: 2,
      splitChannels: splitChannels,
    });

    wavesurferRef.current = ws;

    try {
      regionsPluginRef.current = ws.registerPlugin(
        RegionsPlugin.create({
          dragSelection: false,
        }),
      );
    } catch (e) {
      console.warn('Regions plugin registration failed', e);
    }

    // events
    ws.on('ready', () => {
      const duration = ws.getDuration() || 0;
      setFullTime(formatTime(duration));
      const { timeInterval, primaryLabelInterval } = getTimelineConfig(duration);

      // если плагина ещё нет — регистрируем, иначе пересоздаём
      const timelineOptions = {
        container: timelineRef.current || undefined,
        height: 10,
        notchPercentHeight: 0,
        style: { fontSize: '7px' },
        timeInterval,
        primaryLabelInterval,
        secondaryLabelInterval: 0,
      };

      try {
        if (timelinePluginRef.current) {
          timelinePluginRef.current.destroy?.();
          timelinePluginRef.current = null;
        }

        timelinePluginRef.current = ws.registerPlugin(TimelinePlugin.create(timelineOptions));
      } catch (err) {
        console.warn('Timeline plugin (re)registration failed', err);
      }
    });

    ws.on('play', () => setIsPause(false));
    ws.on('pause', () => {
      setIsPause(true);
      // on pause we clear selection region UI (keeps previous behavior)
      if (selectionRegionRef.current) {
        try {
          selectionRegionRef.current.remove();
        } catch (err) {
          // ignore
        }
        selectionRegionRef.current = null;
      }
    });

    ws.on('seeking', () => {
      // when user seeks, try to detect dialog region
      handleFindChartRegionId();
    });

    ws.on('timeupdate', (time: number) => {
      // time is seconds
      setDurationTime(formatTime(Math.round(time || 0)));
      handleFindChartRegionId();
    });

    return () => {
      try {
        // remove regions we created explicitly
        markerRegionsRef.current.forEach((r) => {
          try {
            r.remove?.();
          } catch (e) {
            console.error(e);
          }
        });
        emotionRegionsRef.current.forEach((r) => {
          try {
            r.remove?.();
          } catch (e) {
            console.error(e);
          }
        });
        selectionRegionRef.current && selectionRegionRef.current.remove?.();
      } catch (err) {
        // ignore
      }
      try {
        timelinePluginRef.current?.destroy?.();
        timelinePluginRef.current = null;
      } catch (e) {
        // ignore
      }
      try {
        ws.destroy();
      } catch (e) {
        console.warn('wavesurfer destroy failed', e);
      }
      wavesurferRef.current = null;
      regionsPluginRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!timelinePluginRef.current) return;
    setTimeout(() => {
      const labels = timelinePluginRef.current.querySelectorAll('[part="timeline-notch-primary"]');
      labels.forEach((el) => {
        (el as HTMLElement).style.color = '#fff';
        (el as HTMLElement).style.fontSize = '12px';
      });
    }, 0);
  }, []);

  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || !audio) return;

    let objectUrl: string | null = null;

    if (audio instanceof Blob) {
      objectUrl = URL.createObjectURL(audio);
      ws.load(objectUrl);
    } else if (typeof audio === 'string') {
      ws.load(audio);
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [audio]);

  const clearMarkerRegions = useCallback(() => {
    markerRegionsRef.current.forEach((r) => {
      try {
        r.remove?.();
      } catch (e) {
        // ignore
      }
    });
    markerRegionsRef.current = [];
  }, []);

  const drawRegions = useCallback(
    (options: { ws: any; regionsPlugin: any; vadMode: boolean }) => {
      const { regionsPlugin, ws, vadMode } = options;
      // eslint-disable-next-line no-debugger
      // debugger;
      changeToggleEmotions(false);
      const iterVariant =
        (vadMode
          ? activeMetricWords?.settings?.metricToShowOnPlayer
          : activeMetricWords?.settings?.ids) || [];
      let index = 1;
      let channel = 0;
      for (const wordIds of iterVariant) {
        if (vadMode) {
          channel = (
            wordIds as {
              to_ms: number;
              channel: number;
              from_ms: number;
            }
          ).channel;
        }
        const from = vadMode
          ? (
              wordIds as {
                to_ms: number;
                channel: number;
                from_ms: number;
              }
            ).from_ms
          : wordsTimeList?.[String(wordIds)]?.from ?? 0;
        const to = vadMode
          ? (
              wordIds as {
                to_ms: number;
                channel: number;
                from_ms: number;
              }
            ).to_ms
          : wordsTimeList?.[String(wordIds)]?.to ?? 0;

        const triangle = document.createElement('div');
        triangle.style.width = '11px';
        triangle.style.height = '22px';
        triangle.style.background = `#${activeMetricWords?.color || ''}`; // цвет флажка
        triangle.style.clipPath = ' polygon(0 0,100% 0,100% 66.66%,50% 100%,0 66.66%)';
        triangle.style.boxShadow = '0 1px 2px rgba(0,0,0,0.25)';
        triangle.style.left = '50%';
        triangle.style.transform = 'translateX(-50%)';
        triangle.style.position = 'absolute';
        if (channel === 0) {
          triangle.style.top = '0px';
        } else if (channel === 1) {
          triangle.style.bottom = '0px';
          triangle.style.transform = 'translateX(-50%) rotate(180deg)';
        }
        // triangle.style.top = '0px';

        // const flagEl = document.createElement('div');
        // flagEl.innerHTML = `<div style="position:absolute; ${
        //   channel === 0 ? 'top:0px;' : 'bottom:0px;'
        // }"></div>`;
        // flagEl.appendChild(triangle);
        // flagEl.append(triangle);
        const timeSec = from / 1000;
        const timeSecEnd = to / 1000;
        const vadRegions = {
          id: `metric-marker-${index}-${Date.now()}`,
          start: timeSec,
          end: vadMode ? timeSecEnd : Math.min(timeSec + 0.01, ws.getDuration() || timeSec), // делаем чуть шире чтобы видно было
          color: `rgba(255, 167, 167, 0.5)`,
          drag: false,
          resize: false,
          data: { type: 'metric-marker' },
        };
        const defaultRegions = {
          id: `metric-marker-${index}-${Date.now()}`,
          start: timeSec,
          color: `#${activeMetricWords?.color || ''}`,
          drag: false,
          resize: false,
          content: triangle,
          data: { type: 'metric-marker' },
        };
        const r = regionsPlugin.addRegion(vadMode ? vadRegions : defaultRegions);
        regionsPlugin.on('region-created', (region) => {
          // небольшой таймаут, чтобы дождаться avoidOverlapping
          setTimeout(() => {
            const wrapper = region.element?.querySelector('[part="region-content"]');
            if (wrapper) {
              wrapper.style.marginTop = '0px';
            }
          }, 20); // 20 мс — чуть позже, чем плагин
        });
        channel = channel === 1 ? 0 : 1;
        markerRegionsRef.current.push(r);
        index++;
      }
    },
    [
      activeMetricWords?.color,
      activeMetricWords?.settings?.ids,
      activeMetricWords?.settings?.metricToShowOnPlayer,
      changeToggleEmotions,
      wordsTimeList,
    ],
  );

  const applyActiveMetricMarkers = useCallback(() => {
    const ws = wavesurferRef.current;
    const regionsPlugin = regionsPluginRef.current;

    if (!ws || !regionsPlugin) return;

    clearMarkerRegions();
    // markersLayer.innerHTML = '';

    const duration = ws.getDuration() || 0;
    if (duration <= 0) {
      ws.once?.('ready', () => applyActiveMetricMarkers());
      return;
    }
    clearMarkerRegions();

    if (activeMetricWords?.settings?.ids && Array.isArray(activeMetricWords.settings.ids)) {
      if (Object.hasOwn(activeMetricWords.settings, 'metricToShowOnPlayer')) {
        drawRegions({ regionsPlugin, ws, vadMode: true });
        return;
      }
      drawRegions({ regionsPlugin, ws, vadMode: false });
    } else {
      changeToggleEmotions(false);
    }
  }, [clearMarkerRegions, activeMetricWords?.settings, drawRegions, changeToggleEmotions]);

  useEffect(() => {
    applyActiveMetricMarkers();
  }, [applyActiveMetricMarkers]);

  const clearEmotionRegions = useCallback(() => {
    emotionRegionsRef.current.forEach((r) => {
      try {
        r.remove?.();
      } catch (e) {
        console.error(e);
      }
    });
    emotionRegionsRef.current = [];
  }, []);

  const applyEmotionRegions = useCallback(() => {
    const regionsPlugin = regionsPluginRef.current;
    if (!regionsPlugin) return;

    clearEmotionRegions();
    if (!emotionTrack || !emotionTrack.length || !toggleEmotions) return;

    for (let i = 0; i < emotionTrack.length; i++) {
      const emotion = emotionTrack[i];
      const start = emotion.from / WAVE_COMPONENT_CONFIG.currentTimingMs;
      const end = emotion.to / WAVE_COMPONENT_CONFIG.currentTimingMs;
      const color = emotionToColorConverter({ tag: emotion.tag, score: emotion.score });
      try {
        const r = regionsPlugin.addRegion({
          id: `emotion-${i}-${Date.now()}`,
          start,
          end,
          color,
          drag: false,
          resize: false,
          data: { systemRegionId: -1, type: 'emotion' },
        });
        emotionRegionsRef.current.push(r);
      } catch (e) {
        // ignore if not ready yet
      }
    }
  }, [emotionTrack, toggleEmotions, clearEmotionRegions]);

  useEffect(() => {
    if (toggleEmotions) applyEmotionRegions();
    else clearEmotionRegions();
  }, [toggleEmotions, applyEmotionRegions, clearEmotionRegions]);

  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || !activeChartTiming) return;
    if (activeChartTiming.from != null) {
      ws.play(activeChartTiming.from / WAVE_COMPONENT_CONFIG.currentTimingMs);
    }
  }, [activeChartTiming]);

  const play = useCallback(() => {
    wavesurferRef.current?.playPause();
  }, []);

  function handleChangeVolume(newVolume: number) {
    wavesurferRef.current?.setVolume(newVolume);
  }
  function handleChangeAudioSpeed(playback: number) {
    wavesurferRef.current?.setPlaybackRate(playback);
  }

  function handleChangeAudioZoom(zoom: number) {
    if (zoom >= WAVE_COMPONENT_CONFIG.zoomLimit[0] && zoom <= WAVE_COMPONENT_CONFIG.zoomLimit[1]) {
      wavesurferRef.current?.setOptions({ minPxPerSec: Math.max(1, zoom) });
      setCurrentZoom(zoom);
    }
  }

  function handleChangeFullScreenModeAndUpdate() {
    changeFullScreen();
    setCurrentZoom(10);
    wavesurferRef.current?.setOptions({ minPxPerSec: 10 });
    setTimeout(() => {
      setCurrentZoom(0);
      wavesurferRef.current?.setOptions({ minPxPerSec: 0 });
    }, 10);
  }

  const renderChannelTitles = Array(WAVE_COMPONENT_CONFIG.channelsLimit)
    .fill('+')
    .map((_, channelIndex) => {
      return (
        <div
          key={channelSettings[channelIndex].settings_channels_id}
          className="basis-1/2 justify-center flex items-center"
        >
          <TooltipWrapper
            content={channelSettings[channelIndex].name}
            id={channelSettings[channelIndex].name}
          >
            <div className="block -rotate-90 text-1color text-[10px] font-bold  whitespace-nowrap">
              {channelSettings[channelIndex].name.length > 10
                ? `${channelSettings[channelIndex].name.slice(0, 8)}...`
                : channelSettings[channelIndex].name}
            </div>
          </TooltipWrapper>
        </div>
      );
    });

  const [contentRef, setContentRef] = useState<HTMLDivElement | null>();
  return (
    <div ref={(ref) => setContentRef(ref)} className="bg-white relative py-4 rounded-lg shadow-lg">
      <div className="inline-flex items-center w-full justify-between mb-[17px] px-[25px]">
        <div className="inline-flex items-center justify-between">
          <h3 className="text-lg font-bold">{t('audoigramm')}</h3>
        </div>
        <div className="inline-flex items-center mt-[5px]">
          <div className="bg-[#F5F5F7] text-3color inline-flex items-center rounded-[10px] text-[15px] px-[10px] py-[3px]">
            <div onClick={() => handleChangeAudioZoom(currentZoom - 10)}>
              <BiZoomOut size={18} className="text-3color cursor-pointer hover:opacity-80" />
            </div>
            <div className="ml-[5px] mr-[9px] w-[33px] text-center select-none">{currentZoom}%</div>
            <div onClick={() => handleChangeAudioZoom(currentZoom + 10)}>
              <BiZoomIn size={18} className="text-3color cursor-pointer hover:opacity-80" />
            </div>
          </div>
          <div className="inline-flex items-center">
            <div className="ml-[18px] z-[1]">
              <CircleButton
                icon="ResizeIcon"
                hintTitle={t('popup_hints.oscillogram_view')}
                size={14}
                onClick={handleChangeFullScreenModeAndUpdate}
                hintPlace="top"
                className="text-3color cursor-pointer hover:opacity-80"
              />
            </div>
            <div className="ml-[15px] z-[1]">
              <CircleButton
                icon="DownloadCloudIcon"
                onClick={() => {
                  if (!recordId) return;
                  downloadAudio({ record_id: recordId });
                }}
                hintPlace="top"
                disable={userInfo?.qm_role === 'operator'}
                hintTitle={t('popup_hints.download_record')}
                size={19}
                className="text-3color cursor-pointer hover:opacity-80"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="px-[5px] min-h-[150px] flex">
        <div className="relative flex-1 bg-gradient-to-t from-white via-bg_components via-50% to-white rounded-l-[13px]">
          <div className="w-[31px] absolute h-full flex flex-col pb-[10px]">
            {renderChannelTitles}
          </div>
          <div
            style={{ maxWidth: (contentRef?.clientWidth || 10) - 10 + 'px' }}
            className="pl-[40px]  overflow-x-auto"
          >
            <div ref={containerRef} id="waveform" />
            <div id="timeline" ref={timelineRef} className="z-1 bg-white rounded-t-[20px]" />
          </div>
        </div>
      </div>

      <div className="inline-flex w-full items-center justify-between mt-[22px] pl-[27px] pr-[22px]">
        <div className="inline-flex items-center">
          <TooltipWrapper content={t('popup_hints.record_play')} id={'record_play_button'}>
            <button className={circleBlueButtonClassName} onClick={play}>
              {isPause ? <BsPlayFill size={19} /> : <BiPause size={19} />}
            </button>
          </TooltipWrapper>
          <div className="bg-gray-100 text-3color justify-center inline-flex text-center min-w-[102px] items-center rounded-[10px] text-[15px] px-[10px] py-[3px]">
            <div className="px-1 text-0color">{durationTime}</div>/
            <div className="px-1 text-3color">{fullTime}</div>
          </div>
          {process.env.REACT_APP_THEME !== 'rtk' && (
            <div className="flex items-center ml-[20px]">
              <Toggle
                checked={toggleEmotions}
                disabled={currentTemplate !== 'voice'}
                onChecked={changeToggleEmotions.bind(null, (prevState: boolean) => !prevState)}
              />
              <span
                className={`flex gap-[3px] items-center text-[12px] ml-[6px] mb-[2px]  ${
                  currentTemplate === 'voice' ? 'text-3color' : 'text-4color'
                }`}
              >
                {t('emotions')}
                {currentTemplate !== 'voice' && (
                  <span className=" flex self-start text-[8px]">({t('soon')})</span>
                )}
              </span>
            </div>
          )}
        </div>
        <div className="inline-flex items-center">
          <div className="mr-[14px] relative z-[10]">
            <PlaybackSpeedSelect
              hint={t('popup_hints.playing_speed')}
              onChange={handleChangeAudioSpeed}
            />
          </div>
          <AudioVolumeRange onChange={handleChangeVolume} />
        </div>
      </div>
    </div>
  );
};

export default connector(WaveAudio);
